import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useTemplateKeywords } from '../../../queries/Templates/useEmailTemplateKeywords';
import { charmGray } from '../../styles/colors';
import { bs } from '../../styles/styles';
import { PillMultiSelect } from '../PillMultiSelect/PillMultiSelect';
import { ShareScope, ShareTemplateCheckbox } from '../ShareTemplateCheckbox/ShareTemplateCheckbox';
import { KeywordIcon } from '../svgs/icons/KeywordIcon';
import { TextInputFormField } from '../TextInputFormField';

interface SaveTemplateModalProps {
	/** Modal title text */
	title: string;
	/** Text for the primary action button */
	cta: string;
	/** Template sharing scope (User or Account level) */
	shareScope: ShareScope;
	/** Callback to update the sharing scope */
	onScopeChange: React.Dispatch<React.SetStateAction<ShareScope>>;
	/** Whether to show keyword selection UI (defaults to true) */
	enableKeywords?: boolean;
	/** Callback when template is saved with name and optional keywords */
	onSave: (name: string, keywords?: string[]) => void;
	/** Callback when cancel button is clicked */
	onCancel: () => void;
}

export const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
	title,
	cta,
	shareScope,
	onScopeChange,
	enableKeywords = true,
	onSave,
	onCancel,
}) => {
	const userSession = useUserSession();
	const [name, setName] = React.useState('');
	const [selectedKeywords, setSelectedKeywords] = React.useState<string[]>([]);

	// Use account industry
	const accountIndustry = userSession.account?.additionalInfo?.industry;
	const keywordsQuery = useTemplateKeywords({
		industry: accountIndustry,
		enabled: enableKeywords,
	});

	const handleKeywordSelect = (keyword: string) => {
		if (keyword && !selectedKeywords.includes(keyword)) {
			setSelectedKeywords([...selectedKeywords, keyword]);
		}
	};

	const handleKeywordRemove = (keyword: string) => {
		setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
	};

	const handleSave = () => {
		const trimmedName = name.trim();
		if (trimmedName) {
			onSave(trimmedName, enableKeywords ? selectedKeywords : undefined);
		}
	};

	const handleCancel = () => {
		onCancel();
	};

	// Reset state when component mounts
	React.useEffect(() => {
		setName('');
		setSelectedKeywords([]);
	}, []);

	return (
		<div className={css(bs.boxBorder)}>
			<div className={css(bs.textTitles, bs.textLg)}>{title}</div>
			<div className={css(bs.pt6)}>
				<div>
					<div className={css(bs.textHeader, bs.textXs)}>
						Template Name:
						<span className='required'>*</span>
					</div>
					<TextInputFormField
						className={css(bs.mt1)}
						onChange={ev => setName(ev.target.value)}
						type='text'
						value={name}
					/>
				</div>
				{enableKeywords ? (
					<div className={css(bs.mt4)}>
						<div className={css(bs.textHeader, bs.textXs, bs.mb2)}>Keywords:</div>
						<PillMultiSelect
							variant='keyword'
							accessory={<KeywordIcon fillColor={charmGray} />}
							addButtonLabel='Add Keyword'
							options={keywordsQuery.data || []}
							selectedOptions={selectedKeywords}
							onEditOptions={handleKeywordSelect}
							onRemove={handleKeywordRemove}
							onOptionClick={handleKeywordSelect}
							disabled={keywordsQuery.isPending}
						/>
					</div>
				) : null}
				<div className={css(bs.mt4)}>
					<ShareTemplateCheckbox shareScope={shareScope} onScopeChange={onScopeChange} />
				</div>
				<div className={css(bs.mt5, bs.flex, bs.itemsCenter, bs.gap2)}>
					<button className={css(bs.ctaButton)} disabled={!name.trim()} onClick={handleSave}>
						{cta}
					</button>
					<button className={css(bs.ctaButtonReverse)} onClick={handleCancel}>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);
};
